<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from '@/i18n'
import { galleryApi } from '@/api'

const { t } = useI18n()

interface GalleryItem {
  id: number
  title: string
  language: string
  format: string
  visual_style: string | null
  thumbnail_url: string | null
  video_url: string | null
  duration: number | null
  author_name: string | null
  created_at: string
}

const items = ref<GalleryItem[]>([])
const loading = ref(true)
const page = ref(1)
const total = ref(0)
const selectedVideo = ref<GalleryItem | null>(null)
const dialogVisible = ref(false)

async function fetchGallery() {
  loading.value = true
  try {
    const response = await galleryApi.getPublic({ page: page.value, page_size: 12 })
    items.value = response.data.items
    total.value = response.data.total
  } catch (error) {
    console.error('Failed to fetch gallery:', error)
    items.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function openVideo(item: GalleryItem) {
  selectedVideo.value = item
  dialogVisible.value = true
}

function closeVideo() {
  dialogVisible.value = false
  selectedVideo.value = null
}

onMounted(fetchGallery)
</script>

<template>
  <div class="gallery-view">
    <div class="gallery-header">
      <h1>{{ t('gallery.title') }}</h1>
      <p>{{ t('gallery.subtitle') }}</p>
    </div>

    <div class="gallery-content">
      <!-- Loading state -->
      <div v-if="loading" class="gallery-loading">
        <div class="loading-grid">
          <div v-for="n in 6" :key="n" class="loading-card">
            <div class="loading-thumbnail"></div>
            <div class="loading-info">
              <div class="loading-title"></div>
              <div class="loading-meta"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="items.length === 0" class="gallery-empty">
        <div class="empty-illustration">
          <div class="empty-circle">
            <span class="empty-icon">🎬</span>
          </div>
          <div class="empty-particles">
            <span>✨</span>
            <span>🎵</span>
            <span>🎨</span>
          </div>
        </div>
        <h2>{{ t('gallery.empty') }}</h2>
        <p>{{ t('gallery.empty.desc') }}</p>
        <router-link to="/login" class="empty-cta">
          {{ t('home.hero.cta') }}
        </router-link>
      </div>

      <!-- Gallery grid -->
      <div v-else class="gallery-grid">
        <div 
          v-for="item in items" 
          :key="item.id" 
          class="gallery-card"
          @click="openVideo(item)"
        >
          <div class="card-thumbnail">
            <img 
              v-if="item.thumbnail_url" 
              :src="item.thumbnail_url" 
              :alt="item.title"
            />
            <div v-else class="thumbnail-placeholder">
              <span>🎬</span>
            </div>
            <div class="card-overlay">
              <span class="play-btn">▶</span>
            </div>
            <div class="card-duration">
              {{ formatDuration(item.duration) }}
            </div>
          </div>
          <div class="card-info">
            <h3 class="card-title">{{ item.title }}</h3>
            <div class="card-meta">
              <span v-if="item.author_name" class="card-author">
                {{ item.author_name }}
              </span>
              <span class="card-style" v-if="item.visual_style">
                {{ item.visual_style }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="total > 12" class="gallery-pagination">
        <el-pagination
          v-model:current-page="page"
          :total="total"
          :page-size="12"
          layout="prev, pager, next"
          @current-change="fetchGallery"
        />
      </div>
    </div>

    <!-- Video Modal -->
    <el-dialog
      v-model="dialogVisible"
      :title="selectedVideo?.title"
      width="90%"
      :before-close="closeVideo"
      class="video-modal"
    >
      <div v-if="selectedVideo" class="video-player">
        <video
          v-if="selectedVideo.video_url"
          :src="selectedVideo.video_url"
          controls
          autoplay
          class="video-element"
        />
        <div v-else class="video-unavailable">
          Video non disponible
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style scoped>
.gallery-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-12) var(--m2v-space-6);
}

.gallery-header {
  text-align: center;
  margin-bottom: var(--m2v-space-12);
}

.gallery-header h1 {
  font-family: var(--m2v-font-display);
  font-size: 2.5rem;
  margin-bottom: var(--m2v-space-4);
}

.gallery-header p {
  color: var(--m2v-text-muted);
  font-size: 1.125rem;
}

.gallery-content {
  max-width: 1400px;
  margin: 0 auto;
}

/* Loading State */
.gallery-loading {
  padding: var(--m2v-space-6);
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--m2v-space-6);
}

.loading-card {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
}

.loading-thumbnail {
  aspect-ratio: 16/9;
  background: linear-gradient(90deg, var(--m2v-bg-elevated) 25%, var(--m2v-border-subtle) 50%, var(--m2v-bg-elevated) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.loading-info {
  padding: var(--m2v-space-4);
}

.loading-title {
  height: 20px;
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-sm);
  margin-bottom: var(--m2v-space-2);
  width: 80%;
}

.loading-meta {
  height: 16px;
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-sm);
  width: 50%;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Empty State */
.gallery-empty {
  padding: var(--m2v-space-12);
  text-align: center;
}

.empty-illustration {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto var(--m2v-space-8);
}

.empty-circle {
  width: 150px;
  height: 150px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-gradient-card);
  border: 2px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-full);
  animation: pulse 3s ease-in-out infinite;
}

.empty-icon {
  font-size: 4rem;
}

.empty-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.empty-particles span {
  position: absolute;
  font-size: 1.5rem;
  animation: float 4s ease-in-out infinite;
}

.empty-particles span:nth-child(1) {
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.empty-particles span:nth-child(2) {
  top: 20%;
  right: 15%;
  animation-delay: 1s;
}

.empty-particles span:nth-child(3) {
  bottom: 20%;
  left: 20%;
  animation-delay: 2s;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(10deg); }
}

.gallery-empty h2 {
  font-size: 1.5rem;
  margin-bottom: var(--m2v-space-3);
  color: var(--m2v-text-primary);
}

.gallery-empty p {
  color: var(--m2v-text-muted);
  font-size: 1.125rem;
  margin-bottom: var(--m2v-space-8);
}

.empty-cta {
  display: inline-flex;
  align-items: center;
  gap: var(--m2v-space-2);
  padding: var(--m2v-space-4) var(--m2v-space-8);
  background: var(--m2v-gradient-hero);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-normal);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
}

.empty-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(99, 102, 241, 0.5);
  color: white;
}

/* Gallery Grid */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--m2v-space-6);
}

.gallery-card {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: all var(--m2v-transition-normal);
}

.gallery-card:hover {
  transform: translateY(-4px);
  border-color: var(--m2v-primary);
  box-shadow: var(--m2v-shadow-lg);
}

.card-thumbnail {
  position: relative;
  aspect-ratio: 16/9;
  background: var(--m2v-bg-elevated);
}

.card-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
}

.card-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity var(--m2v-transition-fast);
}

.gallery-card:hover .card-overlay {
  opacity: 1;
}

.play-btn {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-primary);
  border-radius: var(--m2v-radius-full);
  font-size: 1.5rem;
  color: white;
}

.card-duration {
  position: absolute;
  bottom: var(--m2v-space-2);
  right: var(--m2v-space-2);
  padding: var(--m2v-space-1) var(--m2v-space-2);
  background: rgba(0, 0, 0, 0.8);
  border-radius: var(--m2v-radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.card-info {
  padding: var(--m2v-space-4);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: var(--m2v-space-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  display: flex;
  gap: var(--m2v-space-3);
  font-size: 0.875rem;
  color: var(--m2v-text-muted);
}

.gallery-pagination {
  display: flex;
  justify-content: center;
  margin-top: var(--m2v-space-10);
}

.video-player {
  aspect-ratio: 16/9;
  background: black;
  border-radius: var(--m2v-radius-md);
  overflow: hidden;
}

.video-element {
  width: 100%;
  height: 100%;
}

.video-unavailable {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--m2v-text-muted);
}
</style>
